/**
 * Knowledge Settings Page
 *
 * Tenant-level defaults for knowledge management:
 * - Chunking strategy & parameters
 * - Enrichment flags
 * - Default embedding model
 *
 * Applied to new collections that don't specify custom processing config.
 */

import { useState, useEffect } from 'react';
import { Loader2, Save, BookOpen, Scissors, Sparkles, Binary, RotateCcw } from 'lucide-react';
import { Card } from '@/components/ui';
import { EmbeddingModelSelect } from '@/components/ui/EmbeddingModelSelect';
import { useEmbeddingModels, embeddingProviderLabel } from '@/hooks/use-embedding-models';
import { cn } from '@/lib/utils';
import { useI18n } from '@/i18n';
import { toast } from 'sonner';
import {
  useKnowledgeConfig,
  useUpdateKnowledgeConfig,
  type KnowledgeDefaultsConfig,
  type ChunkingStrategy,
} from '@/hooks/use-knowledge-config';

// =============================================================================
// Constants
// =============================================================================

const STRATEGY_OPTIONS: { value: ChunkingStrategy; label: string; description: string }[] = [
  {
    value: 'semantic_heuristic',
    label: 'Semantic (Heuristic)',
    description: 'Splits by topic boundaries using heuristic rules. Fast and reliable.',
  },
  {
    value: 'semantic_llm',
    label: 'Semantic (LLM)',
    description: 'Uses LLM to identify topic boundaries. Higher quality, slower.',
  },
  {
    value: 'paragraph',
    label: 'Paragraph / Fixed Size',
    description: 'Splits at paragraph boundaries with fixed size limits.',
  },
];

// =============================================================================
// Component
// =============================================================================

export function KnowledgeSettingsPage() {
  const { t } = useI18n();
  const { data: config, isLoading, error } = useKnowledgeConfig();
  const updateMutation = useUpdateKnowledgeConfig();
  const { data: embeddingModels = [], isLoading: loadingModels } = useEmbeddingModels();

  // Local form state
  const [formData, setFormData] = useState<KnowledgeDefaultsConfig | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  // Initialize form when data loads
  useEffect(() => {
    if (config && !formData) {
      setFormData(config);
    }
  }, [config, formData]);

  // Track changes
  useEffect(() => {
    if (config && formData) {
      setIsDirty(JSON.stringify(config) !== JSON.stringify(formData));
    }
  }, [config, formData]);

  const handleSave = async () => {
    if (!formData) return;
    try {
      await updateMutation.mutateAsync(formData);
      setIsDirty(false);
      toast.success(t('admin.settings.knowledge.saveSuccess') || 'Knowledge defaults saved');
    } catch {
      toast.error(t('admin.settings.knowledge.saveError') || 'Failed to save knowledge defaults');
    }
  };

  const handleReset = () => {
    if (config) {
      setFormData(config);
      setIsDirty(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg">
          {t('admin.settings.knowledge.loadError') || 'Failed to load knowledge configuration'}
        </div>
      </div>
    );
  }

  if (!formData) return null;

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-blue-600" />
            {t('admin.settings.knowledge.title') || 'Knowledge Defaults'}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('admin.settings.knowledge.description') || 'Default settings applied to new collections in this workspace.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isDirty && (
            <button
              onClick={handleReset}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              <RotateCcw className="h-4 w-4" />
              {t('common.discard') || 'Discard'}
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={!isDirty || updateMutation.isPending}
            className={cn(
              'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md',
              isDirty
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500 cursor-not-allowed'
            )}
          >
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {t('common.save') || 'Save'}
          </button>
        </div>
      </div>

      {/* Chunking Section */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Scissors className="h-5 w-5 text-orange-500" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('admin.settings.knowledge.chunking.title') || 'Chunking Defaults'}
          </h2>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {t('admin.settings.knowledge.chunking.description') || 'How documents are split into chunks for embedding and retrieval.'}
        </p>

        {/* Strategy */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('admin.settings.knowledge.chunking.strategy') || 'Strategy'}
          </label>
          <div className="space-y-2">
            {STRATEGY_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={cn(
                  'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                  formData.chunking.strategy === opt.value
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                )}
              >
                <input
                  type="radio"
                  name="strategy"
                  value={opt.value}
                  checked={formData.chunking.strategy === opt.value}
                  onChange={() =>
                    setFormData({
                      ...formData,
                      chunking: { ...formData.chunking, strategy: opt.value },
                    })
                  }
                  className="mt-1"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {opt.label}
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {opt.description}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Token Parameters */}
        <div className="grid grid-cols-2 gap-4">
          <NumberField
            label={t('admin.settings.knowledge.chunking.targetTokens') || 'Target Tokens'}
            value={formData.chunking.target_tokens}
            min={100}
            max={1500}
            onChange={(v) =>
              setFormData({
                ...formData,
                chunking: { ...formData.chunking, target_tokens: v },
              })
            }
          />
          <NumberField
            label={t('admin.settings.knowledge.chunking.maxTokens') || 'Max Tokens'}
            value={formData.chunking.max_tokens}
            min={100}
            max={2000}
            onChange={(v) =>
              setFormData({
                ...formData,
                chunking: { ...formData.chunking, max_tokens: v },
              })
            }
          />
          <NumberField
            label={t('admin.settings.knowledge.chunking.minTokens') || 'Min Tokens'}
            value={formData.chunking.min_tokens}
            min={50}
            max={500}
            onChange={(v) =>
              setFormData({
                ...formData,
                chunking: { ...formData.chunking, min_tokens: v },
              })
            }
          />
          <NumberField
            label={t('admin.settings.knowledge.chunking.overlapTokens') || 'Overlap Tokens'}
            value={formData.chunking.overlap_tokens}
            min={0}
            max={200}
            onChange={(v) =>
              setFormData({
                ...formData,
                chunking: { ...formData.chunking, overlap_tokens: v },
              })
            }
          />
        </div>
      </Card>

      {/* Enrichment Section */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="h-5 w-5 text-purple-500" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('admin.settings.knowledge.enrichment.title') || 'Enrichment Defaults'}
          </h2>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {t('admin.settings.knowledge.enrichment.description') || 'AI-powered enhancements applied during document processing.'}
        </p>

        <div className="space-y-4">
          <ToggleField
            label={t('admin.settings.knowledge.enrichment.enrichWithLlm') || 'Enrich with LLM'}
            description={t('admin.settings.knowledge.enrichment.enrichWithLlmDesc') || 'Add AI-generated context to each chunk for better retrieval.'}
            checked={formData.enrichment.enrich_with_llm}
            onChange={(v) =>
              setFormData({
                ...formData,
                enrichment: { ...formData.enrichment, enrich_with_llm: v },
              })
            }
          />
          <ToggleField
            label={t('admin.settings.knowledge.enrichment.extractQuestions') || 'Extract Questions'}
            description={t('admin.settings.knowledge.enrichment.extractQuestionsDesc') || 'Generate potential questions from document content to improve search matching.'}
            checked={formData.enrichment.extract_questions}
            onChange={(v) =>
              setFormData({
                ...formData,
                enrichment: { ...formData.enrichment, extract_questions: v },
              })
            }
          />
          <ToggleField
            label={t('admin.settings.knowledge.enrichment.autoClassify') || 'Auto-classify'}
            description={t('admin.settings.knowledge.enrichment.autoClassifyDesc') || 'Automatically classify documents with taxonomy during processing.'}
            checked={formData.enrichment.auto_classify}
            onChange={(v) =>
              setFormData({
                ...formData,
                enrichment: { ...formData.enrichment, auto_classify: v },
              })
            }
          />
        </div>
      </Card>

      {/* Embedding Section */}
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <Binary className="h-5 w-5 text-green-500" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('admin.settings.knowledge.embedding.title') || 'Embedding Defaults'}
          </h2>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {t('admin.settings.knowledge.embedding.description') || 'Default embedding model for new collections. Can be overridden per collection.'}
        </p>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('admin.settings.knowledge.embedding.model') || 'Embedding Model'}
          </label>
          <EmbeddingModelSelect
            value={formData.embedding.model_id ?? null}
            onChange={(v) =>
              setFormData({
                ...formData,
                embedding: {
                  ...formData.embedding,
                  model_id: v,
                },
              })
            }
            options={embeddingModels}
            isLoading={loadingModels}
            platformDefaultLabel={
              formData.embedding.effective_model_id
                ? `${t('admin.settings.knowledge.embedding.platformDefault') || 'Platform default'} — ${embeddingProviderLabel(formData.embedding.effective_provider ?? '')} · ${formData.embedding.effective_display_name ?? formData.embedding.effective_model_id}`
                : undefined
            }
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t('admin.settings.knowledge.embedding.hint') || 'Leave empty to use the platform default.'}
          </p>
          {formData.embedding.effective_model_id && (
            <p className="mt-2 text-sm bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md px-3 py-2 text-green-800 dark:text-green-200">
              {t('admin.settings.knowledge.embedding.effective') || 'Default in use'}:{' '}
              <span className="font-semibold">
                {embeddingProviderLabel(formData.embedding.effective_provider ?? '')} — {formData.embedding.effective_display_name ?? formData.embedding.effective_model_id}
              </span>
              {formData.embedding.effective_dimensions ? ` (${formData.embedding.effective_dimensions} dims)` : ''}
            </p>
          )}
        </div>
      </Card>

      {/* Info Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <p className="text-sm text-blue-700 dark:text-blue-300">
          {t('admin.settings.knowledge.infoBanner') || 'These defaults are applied when creating new collections without custom configuration. Existing collections are not affected.'}
        </p>
      </div>
    </div>
  );
}

// =============================================================================
// Sub-Components
// =============================================================================

interface NumberFieldProps {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}

function NumberField({ label, value, min, max, onChange }: NumberFieldProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          const v = parseInt(e.target.value, 10);
          if (!isNaN(v) && v >= min && v <= max) {
            onChange(v);
          }
        }}
        className="w-full px-3 py-2 text-sm border rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
      />
      <p className="mt-0.5 text-xs text-gray-400">
        {min} – {max}
      </p>
    </div>
  );
}

interface ToggleFieldProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}

function ToggleField({ label, description, checked, onChange }: ToggleFieldProps) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <div className="relative mt-0.5">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only peer"
        />
        <div
          className={cn(
            'w-9 h-5 rounded-full transition-colors',
            checked ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
          )}
          onClick={() => onChange(!checked)}
        />
        <div
          className={cn(
            'absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform',
            checked && 'translate-x-4'
          )}
          onClick={() => onChange(!checked)}
        />
      </div>
      <div>
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</span>
        <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
      </div>
    </label>
  );
}
